import * as React from "react";
import Box from "@mui/material/Box";
import { Chip, Select, MenuItem, Tooltip, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/system";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ClearIcon from "@mui/icons-material/Clear";

import classes from "./MUISelect.module.css";
import { LinearProgress } from "@mui/material";

const CustomFormControl = styled(FormControl)({
  "& .MuiSelect-select": {
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 14,
  },
});

const MUINativeSelect = React.forwardRef(function MUINativeSelect(
  {
    label,
    options,
    placeholder = "Select",
    optionValue,
    defaultValue = "",
    onChange = () => {},
    onClear,
    loading,
    CustomOption,
    disabled = false,
    ...props
  },
  ref
) {
  const [value, setValue] = React.useState(null);

  React.useImperativeHandle(ref, () => ({
    getValue: () => value,
  }));

  const renderReadOnlyValue = () => {
    if (!optionValue) {
      return (
        <Box sx={{ minHeight: "28px", display: "flex", alignItems: "center" }}>
          <span style={{ color: "#9ca3af", fontStyle: "italic" }}>{placeholder}</span>
        </Box>
      );
    }
    const selectedOption = options.find((opt) => opt.value === optionValue);
    const displayName = selectedOption ? selectedOption.name : optionValue;
    return (
      <Chip
        sx={{
          mt: 0.5,
          height: "auto",
          maxWidth: "100%",
          "& .MuiChip-label": {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.3,
            paddingTop: "4px",
            paddingBottom: "4px",
          },
        }}
        label={displayName}
      />
    );
  };

  // Read-only / disabled state — chip display, no interaction
  if (disabled) {
    return (
      <Box className={classes.box}>
        {label && <Typography className={classes.label}>{label}</Typography>}
        <CustomFormControl fullWidth variant="outlined" size="small">
          <Box
            sx={{
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
              padding: "8px 14px",
              border: "1px solid #e5e7eb",
              borderRadius: "4px",
              backgroundColor: "#f8fafc",
              pointerEvents: "none",
            }}
          >
            {renderReadOnlyValue()}
          </Box>
        </CustomFormControl>
      </Box>
    );
  }

  return (
    <Box className={classes.box}>
      {label && <Typography className={classes.label}>{label}</Typography>}
      <CustomFormControl fullWidth variant="outlined" size="small">
        {loading && <LinearProgress className={classes.progress} />}
        <Select
          value={optionValue ?? ""}
          onChange={(e) => {
            if (e.target.value === "" || e.target.value == null) return;
            onChange(e);
            setValue(e.target.value);
          }}
          displayEmpty
          input={
            <OutlinedInput
              notched
              endAdornment={
                onClear && optionValue !== "" && optionValue != null ? (
                  <InputAdornment
                    position="end"
                    sx={{ marginRight: "18px" }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Clear selection"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          }
          renderValue={(val) => {
            if (val === "" || val == null)
              return (
                <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                  {placeholder}
                </span>
              );
            const opt = options.find((o) => o.value === val);
            return opt ? opt.name : val;
          }}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 320 } },
            MenuListProps: { autoFocusItem: false },
            disableScrollLock: true,
          }}
          ref={ref}
          {...props}
        >
          {options.length > 0 &&
            options.map((option) => {
              const isUnavailable = option.is_visible === false;

              if (isUnavailable) {
                return (
                  <Tooltip
                    key={option.value}
                    title="This model is currently unavailable"
                    placement="right"
                    arrow
                  >
                    <span style={{ display: "block", cursor: "not-allowed" }}>
                      <MenuItem
                        value={option.value}
                        disabled
                        dense
                        sx={{
                          opacity: "0.65 !important",
                          pointerEvents: "none",
                          fontSize: 14,
                          pl: "9px",
                          pr: "9px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <LockOutlinedIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                          <span style={{ color: "#9ca3af" }}>{option.name}</span>
                        </Box>
                      </MenuItem>
                    </span>
                  </Tooltip>
                );
              }

              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  dense
                  sx={{ fontSize: 14, pl: "9px", pr: "9px" }}
                >
                  {CustomOption ? <CustomOption {...option} /> : option.name}
                </MenuItem>
              );
            })}
        </Select>
      </CustomFormControl>
    </Box>
  );
});

export default MUINativeSelect;
